// Config panel for the "check_alarm_active" condition block
// Backend: uses same logic as GET /api/alarms/active/location/:locationId
import React from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Typography,
  FormHelperText,
  Alert
} from '@mui/material';

const CheckAlarmActiveConfig = ({ config, onChange, onSummaryChange }) => {
  const handleChange = (field, value) => {
    const updated = { ...config, [field]: value };
    onChange(updated);
    onSummaryChange(
      updated.scope === 'person'
        ? `Alarm active? (person: ${updated.personId || '?'})`
        : 'Alarm active? (location)'
    );
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="caption" color="text.secondary">
        Checks whether there is a currently active alarm.
        Branches TRUE if an alarm is found, FALSE otherwise.
      </Typography>

      <Alert severity="info" variant="outlined" sx={{ py: 0.5 }}>
        <Typography variant="caption">
          <strong>Location scope</strong> checks alarms linked to the location directly
          (Alarm.Location) <em>and</em> indirectly via persons residing at the
          location (Person.personLocation). An alarm is active when it has no
          finishedAt timestamp.
        </Typography>
      </Alert>

      <FormControl fullWidth size="small">
        <InputLabel>Scope</InputLabel>
        <Select
          value={config.scope || 'location'}
          onChange={(e) => handleChange('scope', e.target.value)}
          label="Scope"
        >
          <MenuItem value="location">Location (direct + person alarms)</MenuItem>
          <MenuItem value="person">Person (alarms for a specific person)</MenuItem>
        </Select>
        <FormHelperText>
          {config.scope === 'person'
            ? 'Checks active alarms linked to a specific person.'
            : 'Uses the automation\'s site locationId — or a locationId extracted from trigger data if available.'
          }
        </FormHelperText>
      </FormControl>

      {config.scope === 'person' && (
        <TextField
          size="small"
          label="Person ID"
          value={config.personId || ''}
          onChange={(e) => handleChange('personId', e.target.value)}
          helperText="The ID of the person to check alarms for. Can use {{varName}} syntax."
          type="number"
        />
      )}

      <TextField
        size="small"
        label="Store Result As Variable"
        value={config.resultVariable || ''}
        onChange={(e) => handleChange('resultVariable', e.target.value.replace(/[^a-zA-Z0-9_]/g, ''))}
        placeholder="e.g. hasAlarm"
        helperText={
          config.resultVariable
            ? `Result (true/false) stored as {{${config.resultVariable}}} for downstream blocks`
            : 'Optional. Store the check result (true/false) as a variable.'
        }
      />

      <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace', fontSize: '0.65rem' }}>
        Endpoint: GET /api/alarms/active/location/:locationId
      </Typography>
    </Box>
  );
};

export default CheckAlarmActiveConfig;
